/* eslint-disable no-underscore-dangle */
import { BlockNoteEditor, BlockSchema } from '@blocknote/core';

export type EditorMode = 'author' | 'student' | 'review';

export interface EditorWithMode<T extends BlockSchema = BlockSchema> extends BlockNoteEditor<T> {
  __mode__: EditorMode;
}

/**
 * Creates a BlockNoteEditor instance and attaches a custom mode to it.
 *
 * This helper allows you to distinguish between editor use cases
 * (e.g., 'author', 'student', or 'review') by storing the mode on the editor instance itself.
 *
 * @template T - The custom block schema used in the editor.
 * @param mode - The editor usage mode ('author', 'student', or 'review').
 * @param options - The same options passed to BlockNoteEditor.create(), including schema and optionally initialContent.
 * @returns A BlockNoteEditor instance with an additional __mode__ property attached.
 */
export const createEditorWithMode = <T extends BlockSchema>(
  mode: EditorMode,
  options: Parameters<typeof BlockNoteEditor.create<T>>[0]
): EditorWithMode<T> => {
  const editor = BlockNoteEditor.create<T>(options) as EditorWithMode<T>;
  editor.__mode__ = mode;
  return editor;
};

// usage with empty as based on editor instance we expect different schemas. In this getter we only need the mode, which isn't tied to the schema.
export const getEditorMode = (editor: BlockNoteEditor<any, any, any>): EditorMode => {
  return (editor as any).__mode__ ?? 'author';
};
